import React, { useState, useEffect, useCallback } from 'react';
import { Typography, TextField, InputAdornment, Box, Button, Dialog, Select, MenuItem, FormControl, IconButton, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress, Alert, Checkbox, FormControlLabel } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAuth } from '../auth/useAuth';
import { useProject } from '../contexts/ProjectContext';
import AdminLayout from '../components/AdminLayout';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import debounce from 'lodash/debounce';
import SmartManualInput from '../components/SmartManualInput';

const TouristList = () => {
  const { api, user } = useAuth();
  const { activeProject, projects } = useProject();
  
  const [tourists, setTourists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isManualInputOpen, setIsManualInputOpen] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [touristToDelete, setTouristToDelete] = useState(null);
  const [touristDeleteInfo, setTouristDeleteInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loadingDeleteInfo, setLoadingDeleteInfo] = useState(false);
  const [showManualOnly, setShowManualOnly] = useState(false);
  const [originFilter, setOriginFilter] = useState('');
  const [returnDestinationFilter, setReturnDestinationFilter] = useState('');

  const fetchTourists = useCallback(async (search, page, pageSize) => {
    try {
      setLoading(true);
      const showUnassigned = activeProject?.id === 'unassigned';
      const response = await api.get('/external-api/tourists', {
        params: {
          search,
          page: page + 1,
          limit: pageSize,
          showUnassigned: showUnassigned ? 'true' : 'false',
          manualOnly: showManualOnly ? 'true' : 'false',
          originFilter: originFilter || undefined,
          returnDestinationFilter: returnDestinationFilter || undefined,
        },
      });
      setTourists(response.data.tourists);
      setTotalRows(response.data.total);
    } catch (error) {
      console.error('נכשל בטעינת רשימת התיירים', error);
    } finally {
      setLoading(false);
    }
  }, [api, activeProject, showManualOnly, originFilter, returnDestinationFilter]);

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      setSystemSettings(response.data);
    } catch (error) {
      console.error('נכשל בטעינת הגדרות המערכת', error);
    }
  }, [api]);

  useEffect(() => {
    fetchSystemSettings();
  }, [fetchSystemSettings]);

  const debouncedFetchTourists = useCallback(
    debounce((search, page, pageSize) => fetchTourists(search, page, pageSize), 300),
    [fetchTourists]
  );

  useEffect(() => {
    debouncedFetchTourists(searchTerm, paginationModel.page, paginationModel.pageSize);
  }, [debouncedFetchTourists, searchTerm, paginationModel, showManualOnly, originFilter, returnDestinationFilter]);

  // רענון כשהפרויקט הפעיל משתנה
  useEffect(() => {
    if (activeProject) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      debouncedFetchTourists(searchTerm, 0, paginationModel.pageSize);
    }
  }, [activeProject, debouncedFetchTourists, searchTerm, paginationModel.pageSize]);

  // פונקציות לטיפול בשינוי הסינונים
  const handleManualOnlyChange = (event) => {
    setShowManualOnly(event.target.checked);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleOriginFilterChange = (event) => {
    setOriginFilter(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleReturnDestinationFilterChange = (event) => {
    setReturnDestinationFilter(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // קבלת אפשרויות מוצא הלוך מההגדרות
  const getOriginOptions = () => {
    if (!systemSettings) return [];
    const departureSettings = systemSettings?.find(s => s.key === 'departure_prices')?.value || {};
    return Object.keys(departureSettings);
  };

  // קבלת אפשרויות יעד חזור מההגדרות
  const getReturnDestinationOptions = () => {
    if (!systemSettings) return [];
    const returnSettings = systemSettings?.find(s => s.key === 'return_prices')?.value || {};
    return Object.keys(returnSettings);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleManualSubmit = async (result) => {
    try {
      const { type, data: manualOrder } = result;
      
      // הוסף את הפרויקט הפעיל להזמנה
      const orderWithProject = {
        ...manualOrder,
        projectId: activeProject?.id && activeProject.id !== 'all' && activeProject.id !== 'unassigned' 
          ? activeProject.id 
          : null
      };
      
      // צור את ההזמנה
      const orderResponse = await api.post('/external-api/manual-add', orderWithProject);
      
      // צור עסקה אם יש מחיר או אם זה תשלום במזומן (גם עם מחיר 0)
      if (manualOrder.calculatedPrice > 0 || manualOrder.paymentMethod === 'מזומן') {
        const transactionData = {
          touristId: orderResponse.data.MISPAR_HAZMANA, // מספר הזמנה של התייר שנוצר
          amount: manualOrder.calculatedPrice || 0, // אם אין מחיר, השתמש ב-0
          originalAmount: manualOrder.originalAmount,
          discountAmount: manualOrder.discountAmount,
          discountReason: manualOrder.discountReason,
          paymentMethod: getPaymentMethodEnum(manualOrder.paymentMethod),
          notes: `תשלום עבור נסיעה ל${manualOrder.originOrDestination}`,
          confirmationKey: manualOrder.confirmationKey, // הוספת ConfirmationKey
          // processedById יתווסף אוטומטית בשרת מהמשתמש המחובר
        };

        await api.post('/transactions', transactionData);
        console.log('עסקה נוצרה בהצלחה:', transactionData);
      }
      
      setIsManualInputOpen(false);
      // רענון רשימת התיירים לאחר הוספה
      fetchTourists(searchTerm, paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      console.error('שגיאה בהוספת הזמנה ידנית:', error);
      // כאן תוכל להוסיף הודעת שגיאה למשתמש
    }
  };

  const getPaymentMethodEnum = (paymentMethod) => {
    switch (paymentMethod) {
      case 'מזומן': return 'cash';
      case 'פאייכלה': return 'paypal';
      case 'אשראי': return 'credit';
      default: return 'cash';
    }
  };

  const handleProjectChange = async (touristId, projectId) => {
    try {
      await api.patch(`/external-api/tourists/${touristId}/project`, { 
        projectId: projectId === 'none' ? null : projectId 
      });
      // רענון רשימת התיירים לאחר עדכון
      fetchTourists(searchTerm, paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      console.error('שגיאה בעדכון פרויקט:', error);
    }
  };

  const fetchTouristDeleteInfo = async (tourist) => {
    try {
      setLoadingDeleteInfo(true);
      // נבדוק כמה תיקופים יש לתייר הזה
      const passengersResponse = await api.get(`/passengers?page=1&limit=100&search=${tourist.MISPAR_HAZMANA}`);
      const relatedPassengers = passengersResponse.data.data || [];
      
      // נבדוק כמה טרנזקציות יש לתייר הזה
      let relatedTransactions = [];
      try {
        const transactionsResponse = await api.get(`/transactions/tourist/${tourist.MISPAR_HAZMANA}`);
        relatedTransactions = transactionsResponse.data || [];
      } catch (error) {
        // אם אין endpoint או שגיאה, נמשיך בלי טרנזקציות
        console.log('לא נמצאו טרנזקציות או שגיאה בטעינה:', error);
        relatedTransactions = [];
      }
      
      // נקבל מידע על האוטובוסים המקושרים
      const busInfo = {};
      for (const passenger of relatedPassengers) {
        if (passenger.busId && !busInfo[passenger.busId]) {
          try {
            const busResponse = await api.get(`/buses/${passenger.busId}`);
            busInfo[passenger.busId] = busResponse.data;
          } catch (error) {
            console.error('שגיאה בטעינת מידע אוטובוס:', error);
            busInfo[passenger.busId] = { busNumber: 'לא ידוע' };
          }
        }
      }
      
      return {
        passengersCount: relatedPassengers.length,
        passengers: relatedPassengers,
        transactionsCount: relatedTransactions.length,
        transactions: relatedTransactions,
        busInfo
      };
    } catch (error) {
      console.error('שגיאה בטעינת מידע מחיקה:', error);
      return {
        passengersCount: 0,
        passengers: [],
        transactionsCount: 0,
        transactions: [],
        busInfo: {}
      };
    } finally {
      setLoadingDeleteInfo(false);
    }
  };

  const handleDeleteClick = async (tourist) => {
    setTouristToDelete(tourist);
    setLoadingDeleteInfo(true);
    setDeleteDialogOpen(true);
    
    const deleteInfo = await fetchTouristDeleteInfo(tourist);
    setTouristDeleteInfo(deleteInfo);
  };

  const handleDeleteConfirm = async () => {
    if (!touristToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/external-api/tourists/${touristToDelete.MISPAR_HAZMANA}`);
      
      // רענון הטבלה
      fetchTourists(searchTerm, paginationModel.page, paginationModel.pageSize);
      
      setDeleteDialogOpen(false);
      setTouristToDelete(null);
    } catch (error) {
      console.error('Failed to delete tourist:', error);
      // כאן אפשר להוסיף הודעת שגיאה למשתמש
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTouristToDelete(null);
    setTouristDeleteInfo(null);
    setLoadingDeleteInfo(false);
  };

  
  const columns = [
    { field: 'MISPAR_HAZMANA', headerName: 'הזמנה', width: 80 },
    { field: 'STATUS_HAZMANA', headerName: 'סטטוס', width: 70 },
    { field: 'MISHPACHA', headerName: 'משפחה', width: 80 },
    { field: 'PRATI', headerName: 'פרטי', width: 70 },
    { field: 'LAST_NAME', headerName: 'Last Name', width: 80 },
    { field: 'FIRST_NAME', headerName: 'First Name', width: 80 },
    {
      field: 'project',
      headerName: 'פרויקט',
      width: 140,
      renderCell: (params) => {
        const currentProjectId = params.row.projectId;
        
        return (
          <FormControl size="small" sx={{ minWidth: 120, mt: 0.5, mb: 0.5 }}>
            <Select
              value={currentProjectId || 'none'}
              onChange={(e) => handleProjectChange(params.row.MISPAR_HAZMANA, e.target.value)}
              displayEmpty
              size="small"
              sx={{
                '& .MuiSelect-select': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            >
              <MenuItem value="none">
                <em>ללא פרויקט</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
    },
    { field: 'NESIOT_HALOCH', headerName: 'נס\' הל', width: 70 },
    { field: 'ORIGIN_HALOCH', headerName: 'מוצא הל', width: 80 },
    { field: 'DESTINATION_HALOCH', headerName: 'יעד הל', width: 80 },
    { field: 'DATE_BUS_HALOCH', headerName: 'תאריך הל', width: 90 },
    { field: 'TIME_BUS_HALOCH', headerName: 'שעה הל', width: 70 },
    { field: 'NESIOT_CHAZOR', headerName: 'נס\' חז', width: 70 },
    { field: 'ORIGIN_CHAZOR', headerName: 'מוצא חז', width: 80 },
    { field: 'DESTINATION_CHAZOR', headerName: 'יעד חז', width: 80 },
    { field: 'DATE_BUS_CHAZOR', headerName: 'תאריך חז', width: 90 },
    { 
      field: 'TIME_BUS_CHAZOR', 
      headerName: 'שעה חז', 
      width: 70,
      valueGetter: (params) => {
        if (!params || !params.row) return '-';
        return params.row.TIME_BUS_CHAZOR || '-';
      }
    },
    { 
      field: 'COMPANY', 
      headerName: 'חברה', 
      width: 90,
      valueGetter: (params) => {
        if (!params || !params.row) return '-';
        const company = params.row.COMPANY;
        if (!company) return '-';
        return company === 'DERECH_TZADIKIM' ? 'דרך צדיקים' : 
               company === 'NETIVIM_TOURS' ? 'נתיבים טורס' : company;
      }
    },
    { 
      field: 'PAYMENT_METHOD', 
      headerName: 'תשלום', 
      width: 80,
      valueGetter: (params) => {
        if (!params || !params.row) return '-';
        return params.row.PAYMENT_METHOD || '-';
      }
    },
    ...(user?.role === 'manager' ? [{
      field: 'actions',
      headerName: 'פעולות',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleDeleteClick(params.row)}
          color="error"
          size="small"
          disabled={deleting}
          sx={{ 
            padding: '8px',
            margin: '4px',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    }] : []),
  ];

  
  // אם המשתמש עדיין לא נטען, הצג טעינה
  if (!user) {
    return (
      <AdminLayout>
        <Typography>טוען...</Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Typography variant="h4" gutterBottom align="center">
        רשימת הזמנות
      </Typography>
      
      {/* סינונים */}
      <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          סינונים
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showManualOnly}
                onChange={handleManualOnlyChange}
                color="primary"
              />
            }
            label="הזמנות ידניות בלבד"
          />
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>מוצא הלוך</Typography>
            <Select
              value={originFilter}
              onChange={handleOriginFilterChange}
              displayEmpty
              size="small"
            >
              <MenuItem value="">
                <em>כל המקורות</em>
              </MenuItem>
              {getOriginOptions().map((origin) => (
                <MenuItem key={origin} value={origin}>
                  {origin}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>יעד חזור</Typography>
            <Select
              value={returnDestinationFilter}
              onChange={handleReturnDestinationFilterChange}
              displayEmpty
              size="small"
            >
              <MenuItem value="">
                <em>כל היעדים</em>
              </MenuItem>
              {getReturnDestinationOptions().map((destination) => (
                <MenuItem key={destination} value={destination}>
                  {destination}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <TextField
          sx={{ flexGrow: 1, mr: 2 }}
          variant="outlined"
          placeholder="חיפוש לפי מספר הזמנה, תז, שם פרטי, שם משפחה.."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsManualInputOpen(true)}
        >
          הוספת הזמנה ידנית
        </Button>
      </Box>

      <Box sx={{ width: '100%', height: 600 }}>
        <DataGrid
            rows={tourists}
            columns={columns}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalRows}
            disableSelectionOnClick
            disableColumnMenu
            loading={loading}
            getRowId={(row) => row.MISPAR_HAZMANA}
            processRowUpdate={(newRow) => newRow}
            autoHeight={false}
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 400,
              '& .MuiDataGrid-cell': {
                paddingTop: '12px',
                paddingBottom: '12px',
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #ddd',
                height: '56px !important',
                minHeight: '56px !important',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-row': {
                minHeight: '56px !important',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                },
                '&:nth-of-type(even)': {
                  backgroundColor: '#fafafa',
                },
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                paddingBottom: '16px',
              },
            }}
          />
      </Box>

      <SmartManualInput 
        open={isManualInputOpen}
        onClose={() => setIsManualInputOpen(false)}
        context="tourist-list"
        systemSettings={systemSettings}
        onValidationComplete={handleManualSubmit}
      />

      {/* דיאלוג אישור מחיקה */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>אישור מחיקה</DialogTitle>
        <DialogContent>
          <DialogContentText>
            האם אתה בטוח שברצונך למחוק את ההזמנה של {touristToDelete?.PRATI || touristToDelete?.FIRST_NAME || ''} {touristToDelete?.MISHPACHA || touristToDelete?.LAST_NAME || ''}?
            <br />
            מספר הזמנה: {touristToDelete?.MISPAR_HAZMANA}
            <br /><br />
            
            {loadingDeleteInfo ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <span>בודק תיקופים מקושרים...</span>
              </Box>
            ) : touristDeleteInfo ? (
              <>
                <strong>מה ימחק:</strong>
                <br />
                • ההזמנה עצמה
                <br />
                {touristDeleteInfo.passengersCount > 0 ? (
                  <>
                    • {touristDeleteInfo.passengersCount} תיקופים מקושרים:
                    <br />
                    {touristDeleteInfo.passengers.map((passenger, index) => (
                      <span key={index}>
                        &nbsp;&nbsp;- תיקוף באוטובוס {touristDeleteInfo.busInfo[passenger.busId]?.busNumber || 'לא ידוע'}
                        <br />
                      </span>
                    ))}
                  </>
                ) : (
                  <>• אין תיקופים מקושרים<br /></>
                )}
                {touristDeleteInfo.transactionsCount > 0 ? (
                  <>
                    • {touristDeleteInfo.transactionsCount} טרנזקציות מקושרות
                    <br />
                  </>
                ) : (
                  <>• אין טרנזקציות מקושרות<br /></>
                )}
                <br />
              </>
            ) : null}
            
            <strong>פעולה זו בלתי הפיכה!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            ביטול
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'מוחק...' : 'מחק'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default TouristList;